import { ReactNode, useEffect, useState } from "react";
import { useSession } from "../services/auth0/session";
import { Navigate, useLocation } from "react-router";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isLoggedIn, hasRole } = useSession();
  const location = useLocation();
  const [redirectCount, setRedirectCount] = useState(0);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Add a delay before redirecting to prevent immediate redirects during auth state transitions
  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      // Start with a small timeout to handle immediate auth state transitions
      const timeout = setTimeout(() => {
        setShouldRedirect(true);
      }, 800); // Give Auth0 some time to establish session

      return () => clearTimeout(timeout);
    } else {
      setShouldRedirect(false);
    }
  }, [isLoading, isLoggedIn]);

  // Track redirect attempts to prevent redirect loops
  useEffect(() => {
    const interval = setInterval(() => {
      if (shouldRedirect) {
        setRedirectCount((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [shouldRedirect]);

  // Prevent more than 3 redirects in quick succession
  const preventRedirectStorm = redirectCount > 3;

  if (isLoading) {
    return (
      <div className="flex flex-1 h-screen justify-center items-center">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  if (!isLoggedIn && shouldRedirect && !preventRedirectStorm) {
    // Save the current location to return after login
    return <Navigate to="/" state={{ returnTo: location.pathname }} replace />;
  }

  if (!hasRole("CUSTOMER")) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}
